import { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * The Horizon Map.
 *
 * A meandering path receding toward a horizon: done behind you, the live step
 * under the light, the next two clear, the far ones deliberately hazy. Time is
 * distance — which is the whole argument. A list of dates tells you what is
 * due; this tells you how far away it is, and that lands differently.
 *
 * Founder's steer: the swerving Candy Crush milestone path, with a rolling
 * horizon so what matters now is nearest and biggest.
 *
 * IT HAS TO SCALE. A hundred-day endeavour can carry twenty milestones, and a
 * fixed number of swerves would either squash them into a straight line or
 * bunch them into noise. So the swerve is measured PER STEP, not per map: the
 * wavelength stays roughly constant and the path simply winds more times when
 * there is more to fit. Node size and label density fall away as the count
 * rises, which is also what makes a long plan readable rather than dense.
 */
const layout = (count) => {
  // roughly one bend every two and a half steps, held between sane limits
  const periods = Math.min(6, Math.max(1.6, count / 2.4));
  // The swerve has to out-argue the climb, or the path reads as a line chart.
  // Bigger amplitude on longer plans, not smaller — a hundred-day endeavour is
  // exactly when "how far away is this" needs to be felt rather than read.
  const amp = count <= 8 ? 62 : count <= 14 ? 56 : 50;
  const pts = [];
  for (let i = 0; i < count; i += 1) {
    const t = count === 1 ? 0 : i / (count - 1);
    const phase = t * Math.PI * periods;
    // damped only gently, so the far bends survive perspective
    const damp = 1 - t * 0.3;
    // wobble in x as well as y — that is what makes it meander rather than
    // ramp. Kept well under the node spacing so the path never doubles back.
    const x = 78 + t * 846 + Math.cos(phase) * 17 * damp;
    const y = 402 - t * 252 + Math.sin(phase) * amp * damp;
    pts.push({ x, y, t, scale: 1 - t * 0.46, fade: 1 - t * 0.5 });
  }
  return pts;
};

/** How many of the near steps can carry a date without colliding. */
const labelEvery = (count) => (count <= 7 ? 1 : count <= 13 ? 2 : 3);

/** Quadratic smoothing through the midpoints — no dependency, no jitter. */
const smooth = (pts) => {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i += 1) {
    const p = pts[i - 1];
    const c = pts[i];
    const mx = (p.x + c.x) / 2;
    const my = (p.y + c.y) / 2;
    d += ` Q ${p.x} ${p.y} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
};

export const HorizonMap = ({ steps = [], onPick, compact = false }) => {
  const pts = useMemo(() => layout(steps.length), [steps.length]);
  const path = useMemo(() => smooth(pts), [pts]);
  const liveIndex = useMemo(() => {
    const i = steps.findIndex((s) => !s.done);
    return i === -1 ? steps.length - 1 : i;
  }, [steps]);

  if (!steps.length) return null;

  return (
    <svg
      viewBox="0 0 1000 500"
      className="absolute inset-0 h-full w-full"
      data-testid="horizon-map"
    >
      <defs>
        <linearGradient id="hm-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a2740" />
          <stop offset="46%" stopColor="#0d1524" />
          <stop offset="100%" stopColor="#07090e" />
        </linearGradient>
        <radialGradient id="hm-sun" cx="50%" cy="0%" r="62%">
          <stop offset="0%" stopColor="rgba(242,200,119,0.5)" />
          <stop offset="60%" stopColor="rgba(242,200,119,0.06)" />
          <stop offset="100%" stopColor="rgba(242,200,119,0)" />
        </radialGradient>
        <linearGradient id="hm-road" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(246,214,150,0.92)" />
          <stop offset="55%" stopColor="rgba(246,214,150,0.42)" />
          <stop offset="100%" stopColor="rgba(246,214,150,0.10)" />
        </linearGradient>
      </defs>

      <rect width="1000" height="500" fill="url(#hm-sky)" />
      <rect width="1000" height="240" fill="url(#hm-sun)" />
      {/* the horizon itself */}
      <line x1="0" y1="118" x2="1000" y2="118" stroke="rgba(246,214,150,0.20)" strokeWidth="1" />

      {/* the road, drawn once on mount so it reads as a journey being laid down */}
      <motion.path
        d={path}
        fill="none"
        stroke="url(#hm-road)"
        strokeWidth={compact ? 5 : 7}
        strokeLinecap="round"
        strokeDasharray="1 0"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1.9, ease: "easeInOut" }}
      />

      {pts.map((p, i) => {
        const s = steps[i];
        const isLive = i === liveIndex;
        const done = Boolean(s.done);
        const many = steps.length > 12;
        const base = many ? 13 : compact ? 15 : 19;
        const r = base * p.scale * (isLive ? 1.42 : 1);
        return (
          <g
            key={s.n}
            onClick={() => onPick && onPick(s)}
            style={{ cursor: onPick ? "pointer" : "default" }}
            data-testid={`horizon-node-${s.n}`}
            opacity={p.fade}
          >
            {isLive && (
              <motion.circle
                cx={p.x}
                cy={p.y}
                r={r}
                fill="none"
                stroke="rgba(246,214,150,0.55)"
                strokeWidth="2"
                initial={{ scale: 1, opacity: 0.75 }}
                animate={{ scale: [1, 2.1], opacity: [0.6, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                style={{ transformOrigin: `${p.x}px ${p.y}px` }}
              />
            )}
            <motion.circle
              cx={p.x}
              cy={p.y}
              r={r}
              fill={done ? "rgba(246,214,150,0.90)" : isLive ? "#f6d696" : "rgba(10,14,22,0.92)"}
              stroke={done || isLive ? "rgba(255,240,205,0.95)" : "rgba(246,214,150,0.5)"}
              strokeWidth={isLive ? 2.5 : 1.6}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.9 + i * 0.11 }}
              style={{ transformOrigin: `${p.x}px ${p.y}px` }}
            />
            {done ? (
              <path
                d={`M ${p.x - r * 0.34} ${p.y} l ${r * 0.28} ${r * 0.3} l ${r * 0.48} ${-r * 0.62}`}
                fill="none"
                stroke="#0b0704"
                strokeWidth={2.2 * p.scale}
                strokeLinecap="round"
              />
            ) : (
              <text
                x={p.x}
                y={p.y + 4.5 * p.scale}
                textAnchor="middle"
                fontSize={16 * p.scale}
                fontFamily="ui-monospace, monospace"
                fill={isLive ? "#0b0704" : "rgba(246,214,150,0.85)"}
                fontWeight={isLive ? 700 : 400}
              >
                {s.n}
              </text>
            )}

            {/* only the near steps carry words. The far ones are meant to be
                vague, and on a long plan only every second or third is dated so
                the labels never collide. */}
            {p.t < 0.55 && (i % labelEvery(steps.length) === 0 || isLive) && (
              <text
                x={p.x}
                y={p.y + r + 17 * p.scale}
                textAnchor="middle"
                fontSize={13 * p.scale}
                fontFamily="ui-monospace, monospace"
                fill="rgba(246,214,150,0.62)"
                letterSpacing="1.4"
              >
                {(s.window || "").split("·")[0].trim().toUpperCase()}
              </text>
            )}
          </g>
        );
      })}

      <text
        x="24"
        y="472"
        fontSize="15"
        fontFamily="ui-monospace, monospace"
        fill="rgba(246,214,150,0.42)"
        letterSpacing="2.4"
      >
        NOW
      </text>
      <text
        x="976"
        y="100"
        textAnchor="end"
        fontSize="15"
        fontFamily="ui-monospace, monospace"
        fill="rgba(246,214,150,0.42)"
        letterSpacing="2.4"
      >
        HORIZON
      </text>
    </svg>
  );
};

export default HorizonMap;
